import {Checkbox, Col, Collapse, Row} from "antd"
import styles from "./WorkDayItem.module.css"
import {BaseTimePicker} from "components/DateTimePicker"
import {useModel} from "hooks"
import {Auth} from "types/auth"
import {getDisabledEndHours, getDisabledEndMinutes} from "helpers/calendar"
import cx from "classnames"
import {useEffect, useMemo, useState} from "react"
import moment, {Moment} from "moment"
import {BaseInput, Icon} from "components"
import {AddItemCircleButton} from "components/buttons"
import {BreakTimeFieldErrors, BreakTimeForm, createDefaultBreakTime, WorkDay} from "types/work-schedule"
import {BreakTimeItem} from "./BreakTimeItem"

const {Panel} = Collapse

type Props = {
    item: WorkDay
    errors?: any
    isShiftSetting?: boolean
    disabled?: boolean
    breakTimeFieldErrors?: Record<number, BreakTimeFieldErrors>
    onChange: (_workday: WorkDay) => void
}

export function WorkDayItem(props: Props) {
    const {item, errors, onChange, isShiftSetting, disabled, breakTimeFieldErrors = {}} = props
    const model = useModel()
    const [workDay, setWorkDay] = useState<WorkDay>(item)
    const [breakTimesActiveKey, setBreakTimesActiveKey] = useState<string | string[]>([])

    useEffect(() => {
        setWorkDay(item)
    }, [item])

    const onChangeWorkDay = (field: keyof WorkDay, value: any) => {
        const _workDay = {
            ...workDay,
            [field]: value
        }
        if (field === "startTime" && value) {
            const isSameDate = moment(value).add(9, "hour").startOf("date").isSame(moment(value).startOf("date"))
            _workDay.endTime = isSameDate ? moment(value).add(9, "hour") : moment(value).endOf("date")
        }

        if (field === "maxHoursEnabled" && isShiftSetting) {
            if (!value) {
                _workDay.maxHours = undefined
            } else {
                _workDay.maxHours = 0
            }
        }

        if (field === "bonusMinutesEnabled" && isShiftSetting) {
            if (!value) {
                _workDay.bonusMinutesBefore = undefined
                _workDay.bonusMinutesAfter = undefined
            } else {
                _workDay.bonusMinutesBefore = 0
                _workDay.bonusMinutesAfter = 0
            }
        }

        if (field === "enabled" && isShiftSetting && !value) {
            _workDay.maxHoursEnabled = false
            _workDay.maxHours = undefined
        }

        if (field === "enabled" && isShiftSetting && !value) {
            _workDay.bonusMinutesEnabled = false
            _workDay.bonusMinutesBefore = undefined
            _workDay.bonusMinutesAfter = undefined
        }

        if (field === "enabled" && isShiftSetting && !value) {
            _workDay.distEdEnabled = false
        }

        setWorkDay(_workDay)
        onChange(_workDay)
    }

    const onChangeBreakTime = (index: number, breakTime: BreakTimeForm) => {
        const breakTimes = [...(workDay.breakTimes || [])]
        breakTimes[index] = breakTime
        onChangeWorkDay("breakTimes", breakTimes)
    }

    const onAddBreakTime = () => {
        const breakTimes = [...(workDay.breakTimes || []), createDefaultBreakTime()]
        onChangeWorkDay("breakTimes", breakTimes)
    }

    const onRemoveBreakTime = (index: number) => {
        const breakTimes = (workDay.breakTimes || []).filter((_, i) => i !== index)
        onChangeWorkDay("breakTimes", breakTimes)
    }

    const checkboxDisabled = useMemo(() => !workDay.enabled || workDay.allDay, [workDay.enabled, workDay.allDay])
    const breakTimesCount = workDay.breakTimes?.length ?? 0
    const breakTimesHeader = breakTimesCount > 0 ? `Break Times (${breakTimesCount})` : "Break Times"
    const hasBreakTimeFieldErrors = Object.keys(breakTimeFieldErrors).length > 0

    useEffect(() => {
        if (hasBreakTimeFieldErrors) {
            setBreakTimesActiveKey(["break-times"])
        }
    }, [hasBreakTimeFieldErrors, breakTimeFieldErrors])

    return (
        <div className={styles.workDayContainer}>
            <Row gutter={[8, 8]} className={styles.container}>
                <Col span={6}>
                    <Checkbox
                        checked={workDay.enabled}
                        onChange={(event) => onChangeWorkDay("enabled", event.target.checked)}
                        className={cx(styles.label, {[styles.labelDisabled]: !workDay.enabled})}>
                        {workDay.name}
                    </Checkbox>
                </Col>
                <Col span={18} className={styles.allDayContainer}>
                    <Checkbox
                        checked={workDay.allDay}
                        onChange={(e) => onChangeWorkDay("allDay", e.target.checked)}
                        disabled={!workDay.enabled}
                        className={styles.allDay}>
                        ALL DAY
                    </Checkbox>
                    {isShiftSetting ? (
                        <Checkbox
                            checked={workDay.distEdEnabled}
                            disabled={!workDay.enabled}
                            onChange={(event) => onChangeWorkDay("distEdEnabled", event.target.checked)}
                            className={styles.allDay}>
                            DistEd
                        </Checkbox>
                    ) : null}
                </Col>
            </Row>
            {isShiftSetting && workDay.enabled ? (
                <div className={styles.extraSettingContainer}>
                    <Row gutter={[8, 8]} className={styles.extraSetting}>
                        <Col span={8}>
                            <span className={cx(styles.label, {[styles.labelDisabled]: !workDay.enabled})}>
                                MAX HOURS
                            </span>
                            <Checkbox
                                checked={workDay.maxHoursEnabled}
                                disabled={!workDay.enabled}
                                onChange={(event) => onChangeWorkDay("maxHoursEnabled", event.target.checked)}
                                className={cx(styles.label, {[styles.labelDisabled]: !workDay.enabled})}>
                                <BaseInput
                                    min={0}
                                    type="number"
                                    onChange={(text) => onChangeWorkDay("maxHours", text)}
                                    placeholder={"Max Hours"}
                                    disabled={!workDay.maxHoursEnabled}
                                    readOnly={!workDay.maxHoursEnabled}
                                    value={workDay.maxHours}
                                    error={errors?.includes("maxHours")}
                                />
                            </Checkbox>
                        </Col>
                        <Col span={16}>
                            <span className={cx(styles.label, {[styles.labelDisabled]: !workDay.enabled})}>
                                BONUS MINUTES
                            </span>
                            <div className={styles.bonusContainer}>
                                <Checkbox
                                    checked={workDay.bonusMinutesEnabled}
                                    disabled={!workDay.enabled}
                                    onChange={(event) => onChangeWorkDay("bonusMinutesEnabled", event.target.checked)}
                                    style={{
                                        marginTop: 11
                                    }}
                                    className={cx(styles.label, {[styles.labelDisabled]: !workDay.enabled})}
                                />
                                <div className={styles.bonusInputContainer}>
                                    <span className={cx(styles.subLabel, {[styles.labelDisabled]: !workDay.enabled})}>
                                        Before
                                    </span>
                                    <BaseInput
                                        min={0}
                                        type="number"
                                        onChange={(text) => onChangeWorkDay("bonusMinutesBefore", text)}
                                        placeholder={"Before"}
                                        disabled={!workDay.bonusMinutesEnabled}
                                        readOnly={!workDay.bonusMinutesEnabled}
                                        value={workDay.bonusMinutesBefore}
                                        error={errors?.includes("bonusMinutesBefore")}
                                    />
                                </div>
                                <div className={styles.bonusInputContainer}>
                                    <span className={cx(styles.subLabel, {[styles.labelDisabled]: !workDay.enabled})}>
                                        After
                                    </span>
                                    <BaseInput
                                        min={0}
                                        type="number"
                                        onChange={(text) => onChangeWorkDay("bonusMinutesAfter", text)}
                                        placeholder={"After"}
                                        disabled={!workDay.bonusMinutesEnabled}
                                        readOnly={!workDay.bonusMinutesEnabled}
                                        value={workDay.bonusMinutesAfter}
                                        error={errors?.includes("bonusMinutesAfter")}
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={[8, 8]} className={styles.extraSetting}>
                        <span className={styles.subLabel}>
                            Bonus minutes only work when time clock tracking is "Base on scheduled hours only"
                        </span>
                    </Row>
                </div>
            ) : null}
            {(workDay.enabled && isShiftSetting) || !isShiftSetting ? (
                <div className={styles.timeRange}>
                    <BaseTimePicker
                        value={workDay.startTime || undefined}
                        onChange={(newValue) => onChangeWorkDay("startTime", newValue)}
                        placeholder="From"
                        format={model.user.timeFormat === Auth.TimeFormat.Time_12 ? "hh:mm A" : "HH:mm"}
                        error={errors?.includes("startTime")}
                        disabled={checkboxDisabled}
                        suffixIcon={
                            <div
                                className={cx(styles.calendarIconWrap, {
                                    [styles.disabledCalenderIcon]: checkboxDisabled
                                })}>
                                <Icon icon="TIME_CLOCK_OUTLINE" color="#fff" />
                            </div>
                        }
                        className={styles.timePicker}
                        size="small"
                    />
                    <BaseTimePicker
                        value={workDay.endTime || undefined}
                        onChange={(newValue) => onChangeWorkDay("endTime", newValue)}
                        disabledHours={() => getDisabledEndHours(workDay.startTime)}
                        disabledMinutes={() => getDisabledEndMinutes(workDay.startTime, workDay.endTime)}
                        placeholder="To"
                        format={model.user.timeFormat === Auth.TimeFormat.Time_12 ? "hh:mm A" : "HH:mm"}
                        error={errors?.includes("endTime")}
                        disabled={checkboxDisabled}
                        suffixIcon={
                            <div
                                className={cx(styles.calendarIconWrap, {
                                    [styles.disabledCalenderIcon]: checkboxDisabled
                                })}>
                                <Icon icon="TIME_CLOCK_OUTLINE" color="#fff" />
                            </div>
                        }
                        className={styles.timePicker}
                        size="small"
                    />
                </div>
            ) : null}

            {isShiftSetting && workDay.enabled ? (
                <Collapse
                    className={cx(styles.breakTimesAccordion, {
                        [styles.breakTimesAccordionInteractive]: disabled,
                        [styles.breakTimesAccordionError]: hasBreakTimeFieldErrors
                    })}
                    activeKey={breakTimesActiveKey}
                    onChange={setBreakTimesActiveKey}
                    expandIconPosition="right">
                    <Panel header={breakTimesHeader} key="break-times" className={styles.breakTimesPanel}>
                        <div
                            className={cx(styles.breakTimesContent, {
                                [styles.breakTimesContentDisabled]: disabled
                            })}>
                            {(workDay.breakTimes || []).map((breakTime, index) => (
                                <BreakTimeItem
                                    key={index}
                                    item={breakTime}
                                    disabled={disabled}
                                    fieldErrors={breakTimeFieldErrors[index]}
                                    onChange={(updated) => onChangeBreakTime(index, updated)}
                                    onRemove={() => onRemoveBreakTime(index)}
                                />
                            ))}
                            {!disabled ? (
                                <AddItemCircleButton
                                    title="Add Break Time"
                                    onClick={onAddBreakTime}
                                    className={styles.addBreakTimeBtn}
                                />
                            ) : null}
                        </div>
                    </Panel>
                </Collapse>
            ) : null}

            {isShiftSetting ? <hr className={styles.line} /> : null}
        </div>
    )
}
